import {
	ENROLLMENT_LIST_TAG,
	LIST_TAG,
	SEQUENCE_LIST_TAG,
	remindersApi,
	type Reminder,
	type ReminderAssignee,
	type ReminderCustomer,
	type ReminderPriority,
	type SequenceAction,
} from './remindersApi';
import type { Notifications } from '@/components/notifications/notificationSettings';

// Reminder sequences: reusable follow-up plans whose steps become reminders
// when a customer is added. Shares the reminders cache so completing a step
// refreshes the customer's timeline. See docs/reminder-sequences.md.

export interface ReminderSequenceStepTemplate {
	_id?: string;
	day: number;
	order?: number;
	action: SequenceAction;
	title: string;
	notes?: string;
	/** 'HH:mm'; missing means the sequence's usual time */
	time?: string;
	/** Day 1 only: due the moment the customer is added */
	immediate?: boolean;
	priority?: ReminderPriority;
	_assignees?: string[];
}

export type SequenceAssignTo = 'adder' | 'specific';

export type SequenceTriggerType = 'pipeline_stage' | 'form_submission' | 'new_lead';

/** Where a new lead first got in touch; a new_lead trigger with none counts every channel */
export type LeadChannel =
	| 'sms'
	| 'webchat'
	| 'form'
	| 'facebook'
	| 'instagram'
	| 'google'
	| 'facebook_lead_ads'
	| 'email';

/** What the server accepts for a trigger on create / PATCH */
export interface ReminderSequenceTriggerInput {
	type: SequenceTriggerType;
	/** pipeline_stage */
	_pipeline?: string;
	columnId?: string;
	/** form_submission */
	_questionnaire?: string;
	/** new_lead; empty means every channel */
	channels?: LeadChannel[];
}

/** A saved trigger: "Start automatically" when this happens */
export interface ReminderSequenceTrigger extends ReminderSequenceTriggerInput {
	_id: string;
	/** e.g. "Sales pipeline → Quoted", "Contact us form", "New lead · Text, Website chat" */
	label: string;
	/** Its pipeline, column or form no longer exists */
	isMissing?: boolean;
}

export interface ReminderSequence {
	_id: string;
	name: string;
	description?: string;
	steps: ReminderSequenceStepTemplate[];
	defaultTime: string;
	assignTo: SequenceAssignTo;
	_assignees?: string[];
	notifications: Notifications;
	skipClosedDays: boolean;
	/** Remove the steps that aren't done when the customer replies */
	stopOnReply?: boolean;
	triggers?: ReminderSequenceTrigger[];
	createdAt: string;
	updatedAt: string;
	activeCount: number;
	dueTodayCount: number;
	overdueCount: number;
}

export type ReminderSequenceInput = Pick<
	ReminderSequence,
	'name' | 'steps' | 'defaultTime' | 'assignTo' | 'notifications' | 'skipClosedDays'
> & {
	description?: string;
	_assignees?: string[];
	/** Left out, the server keeps what's stored */
	stopOnReply?: boolean;
	triggers?: ReminderSequenceTriggerInput[];
};

export interface PreviewStep {
	stepId: string;
	stepNumber: number;
	day: number;
	action: SequenceAction;
	title: string;
	immediate: boolean;
	dueAt: string;
	/** Moved off a closed day */
	moved: boolean;
}

export interface PreviewResponse {
	error: boolean;
	timezone: string;
	today: string;
	startDate: string;
	steps: PreviewStep[];
}

export type PreviewRequest =
	| { sequenceId: string; startDate?: string }
	| (Partial<ReminderSequenceInput> & { startDate?: string });

export type EnrollmentStatus = 'active' | 'completed' | 'stopped';
export type EnrollmentSource =
	| 'reminders'
	| 'conversation'
	| 'composer'
	| 'pipeline'
	| 'sequence'
	| 'automation';
/** Where someone added the customer by hand; 'automation' is the server's own */
export type ManualEnrollmentSource = Exclude<EnrollmentSource, 'automation'>;
export type EnrollmentStopReason =
	| 'removed'
	| 'sequence_deleted'
	| 'customer_deleted'
	| 'merged'
	| 'undone'
	| 'customer_replied';

export interface EnrollmentStep {
	stepId: string;
	stepNumber: number;
	day: number;
	action: SequenceAction;
	title: string;
	/** The step's reminder was deleted */
	removed: boolean;
	reminder: Reminder | null;
}

export interface Enrollment {
	_id: string;
	_reminderSequence: string;
	sequenceName: string;
	_customer: ReminderCustomer | string;
	_pipelineItem?: string | null;
	source?: EnrollmentSource;
	/** What started an automatic run */
	trigger?: { _id: string; type: SequenceTriggerType; label: string } | null;
	stopOnReply?: boolean;
	status: EnrollmentStatus;
	startDate: string;
	createdAt: string;
	completedAt?: string | null;
	stoppedAt?: string | null;
	stopReason?: EnrollmentStopReason;
	_addedBy?: ReminderAssignee | null;
	_stoppedBy?: ReminderAssignee | null;
	stepCount: number;
	/** The earliest open step, or null when none is open */
	currentStepNumber: number | null;
	steps: EnrollmentStep[];
}

export interface EnrollBody {
	sequenceId: string;
	_customer: string;
	startDate?: string;
	_assignees?: string[];
	_pipelineItem?: string;
	source: ManualEnrollmentSource;
}

export interface NextStep {
	reminder: Pick<Reminder, '_id' | 'text' | 'dateTime' | 'sequence'> & { _customer: string };
	activeCount: number;
}

const sequenceTag = (_id: string) => ({ type: 'ReminderSequence' as const, id: _id });

export const reminderSequencesApi = remindersApi.injectEndpoints({
	endpoints: (builder) => ({
		getReminderSequences: builder.query<{ error: boolean; sequences: ReminderSequence[] }, void>({
			query: () => ({ url: '/api/reminder-sequences' }),
			providesTags: (result) => [
				...(result?.sequences ?? []).map(({ _id }) => sequenceTag(_id)),
				SEQUENCE_LIST_TAG,
			],
		}),
		previewReminderSequence: builder.query<PreviewResponse, PreviewRequest>({
			query: (body) => ({ url: '/api/reminder-sequences/preview', method: 'POST', body }),
			// A changed template changes its dates.
			providesTags: (_result, _error, arg) =>
				'sequenceId' in arg && arg.sequenceId ? [sequenceTag(arg.sequenceId)] : [],
			keepUnusedDataFor: 30,
		}),
		createReminderSequence: builder.mutation<
			{ error: boolean; sequence: ReminderSequence },
			ReminderSequenceInput
		>({
			query: (body) => ({ url: '/api/reminder-sequences', method: 'POST', body }),
			invalidatesTags: [SEQUENCE_LIST_TAG],
		}),
		updateReminderSequence: builder.mutation<
			{ error: boolean; sequence: ReminderSequence; updatedEnrollments: number },
			{ _id: string; body: ReminderSequenceInput & { applyToExisting?: boolean } }
		>({
			query: ({ _id, body }) => ({ url: `/api/reminder-sequences/${_id}`, method: 'PATCH', body }),
			invalidatesTags: (_result, _error, { _id, body }) => [
				sequenceTag(_id),
				SEQUENCE_LIST_TAG,
				...(body.applyToExisting ? [LIST_TAG, ENROLLMENT_LIST_TAG] : []),
			],
		}),
		duplicateReminderSequence: builder.mutation<{ error: boolean; sequence: ReminderSequence }, string>({
			query: (_id) => ({ url: `/api/reminder-sequences/${_id}/duplicate`, method: 'POST' }),
			invalidatesTags: [SEQUENCE_LIST_TAG],
		}),
		deleteReminderSequence: builder.mutation<
			{ error: boolean; _id: string; stoppedEnrollments: number },
			string
		>({
			query: (_id) => ({ url: `/api/reminder-sequences/${_id}`, method: 'DELETE' }),
			invalidatesTags: [SEQUENCE_LIST_TAG, LIST_TAG, ENROLLMENT_LIST_TAG],
		}),
		enrollCustomer: builder.mutation<{ error: boolean; enrollment: Enrollment }, EnrollBody>({
			query: ({ sequenceId, ...body }) => ({
				url: `/api/reminder-sequences/${sequenceId}/enrollments`,
				method: 'POST',
				body,
			}),
			invalidatesTags: [SEQUENCE_LIST_TAG, LIST_TAG, ENROLLMENT_LIST_TAG],
		}),
		getEnrollments: builder.query<
			{ error: boolean; enrollments: Enrollment[] },
			{ _customer?: string; _reminderSequence?: string; status?: EnrollmentStatus | 'all' }
		>({
			query: (params) => ({ url: '/api/reminder-sequence-enrollments', params }),
			providesTags: [ENROLLMENT_LIST_TAG],
		}),
		getNextSequenceSteps: builder.query<
			{ error: boolean; nextSteps: Record<string, NextStep> },
			string[]
		>({
			query: (customerIds) => ({
				url: '/api/reminder-sequence-enrollments/next-steps',
				method: 'POST',
				body: { customerIds },
			}),
			providesTags: [ENROLLMENT_LIST_TAG, LIST_TAG],
		}),
		stopEnrollment: builder.mutation<{ error: boolean; enrollment: Enrollment }, string>({
			query: (_id) => ({ url: `/api/reminder-sequence-enrollments/${_id}/stop`, method: 'POST' }),
			invalidatesTags: [SEQUENCE_LIST_TAG, LIST_TAG, ENROLLMENT_LIST_TAG],
		}),
		undoEnrollment: builder.mutation<{ error: boolean; _id: string }, string>({
			query: (_id) => ({ url: `/api/reminder-sequence-enrollments/${_id}`, method: 'DELETE' }),
			invalidatesTags: [SEQUENCE_LIST_TAG, LIST_TAG, ENROLLMENT_LIST_TAG],
		}),
	}),
});

export const {
	useGetReminderSequencesQuery,
	usePreviewReminderSequenceQuery,
	useCreateReminderSequenceMutation,
	useUpdateReminderSequenceMutation,
	useDuplicateReminderSequenceMutation,
	useDeleteReminderSequenceMutation,
	useEnrollCustomerMutation,
	useGetEnrollmentsQuery,
	useGetNextSequenceStepsQuery,
	useStopEnrollmentMutation,
	useUndoEnrollmentMutation,
} = reminderSequencesApi;
